import type { FileRef } from '@/types/file';
import { apiClient } from './client';

/**
 * Per-network white-label branding resolved from the request host.
 *
 * Public, unauthenticated endpoint — used pre-auth on the login pages where the
 * only signal is the subdomain. The frontend passes its own hostname explicitly
 * (decided: avoids CDN / X-Forwarded-Host ambiguity; branding is non-sensitive).
 * See network-branding-frontend-plan.md / vingapi docs/network-branding-plan.md.
 */
export interface Branding {
  /** Matched network's id, or null when no network matches the host. */
  networkId: number | null;
  /** Network slug — the handle the NOVI SSO login path routes on. Null when no match. */
  identifier: string | null;
  name: string | null;
  brandingEnabled: boolean;
  /** 3:1 banner logo (Thumbor), for the auth pages. Null when unset. */
  logo: FileRef | null;
  /** Transparent header logo (served raw, not Thumbor), for the app header. Null when unset. */
  headerLogo: FileRef | null;
  /**
   * Whether this host's network offers NOVI SSO — an independent gate from
   * branding. Drives the "Continue with {noviSsoLabel}" login button.
   */
  noviSsoEnabled: boolean;
  /** Brand word in "Continue with {label}" (backend defaults to the slug, uppercased). */
  noviSsoLabel: string | null;
  /** The association's uploaded NOVI button icon (raw asset). Null when unset. */
  noviSsoLogo: FileRef | null;
}

/**
 * Resolve branding for a host. Returns `brandingEnabled: false` when the host
 * has no enabled branding, so callers fall back to default Ving branding.
 *
 * GET /api/branding/?host=<hostname>
 */
export async function getBranding(host: string): Promise<Branding> {
  const response = await apiClient.get<Branding>('/api/branding/', { params: { host } });
  return response.data;
}
